// LiveTripChart.jsx
import React, { useState, useEffect } from "react";
import { Box, Grid, Card, CircularProgress, Button, Switch, FormControlLabel } from "@mui/material";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftBox from "components/SoftComponents/SoftBox";
import { Line } from "react-chartjs-2";
import moment from "moment-timezone"; // <-- Changed to moment-timezone
import Swal from "sweetalert2";
import { getLiveTripDetail, postArrived, postAnomaly } from "modules/stopmonitor/api";
import annotationPlugin from 'chartjs-plugin-annotation';
import { Chart } from 'chart.js';
Chart.register(annotationPlugin);

const TZ = "Australia/Sydney"; // Define the target timezone

export default function LiveTripChart({ stopId, matchKey }) {
    const [tripData, setTripData] = useState(null);
    const [loading, setLoading] = useState(true);

    const [showSecondsAway, setShowSecondsAway] = useState(false);
    const [showAnnotationLabels, setShowAnnotationLabels] = useState(true);

    // Poll the specific trip's live telemetry every 5 seconds
    useEffect(() => {
        let isMounted = true;

        const fetchDetail = async () => {
            try {
                const data = await getLiveTripDetail(matchKey, stopId);
                if (isMounted && data) {
                    setTripData(data);
                    setLoading(false);
                }
            } catch (err) {
                console.error("Failed to load live trip details:", err);
            }
        };

        fetchDetail();
        const interval = setInterval(fetchDetail, 5000);
        return () => {
            isMounted = false;
            clearInterval(interval);
        };
    }, [matchKey, stopId]);

    // ACTION HANDLERS FOR THE CHART VIEW
    const handleTriggerArrival = async () => {
        const currentEpoch = moment().unix();
        // Enforce timezone here
        const formattedTime = moment.unix(currentEpoch).tz(TZ).format("h:mm:ss a");

        const result = await Swal.fire({
            title: "Confirm Vehicle Arrival?",
            html: `
        <div style="text-align: left; font-family: sans-serif; font-size: 14px; color: #475569;">
          <p style="margin-bottom: 8px;"><strong>Route:</strong> Route ${tripData.routeShortName}</p>
          <p style="margin-bottom: 8px;"><strong>Destination:</strong> ${tripData.tripHeadsign}</p>
          <p style="margin-bottom: 8px;"><strong>Arrival Metric:</strong> ${formattedTime}</p>
        </div>
      `,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Confirm",
        });

        if (result.isConfirmed) {
            try {
                await postArrived(matchKey, stopId);
                setTripData(prev => ({ ...prev, arrivalEpoch: currentEpoch }));
                Swal.fire({ title: "Logged!", text: "Arrival flagged successfully.", icon: "success", timer: 1500, showConfirmButton: false });
            } catch (e) {
                Swal.fire("Error", "Could not log arrival.", "error");
            }
        }
    };

    const handleTriggerAnomaly = async () => {
        const result = await Swal.fire({
            title: "Report Stream Anomaly?",
            html: `
        <div style="text-align: left; font-family: sans-serif; font-size: 14px; color: #475569;">
          <p><strong>Route Name:</strong> Route ${tripData.routeShortName}</p>
          <p>Flag this feed data as erratic or severely decoupled from reality?</p>
        </div>
      `,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#b91c1c",
            confirmButtonText: "Report",
        });

        if (result.isConfirmed) {
            try {
                await postAnomaly(matchKey, stopId);
                setTripData(prev => ({ ...prev, isAnomaly: true }));
                Swal.fire({ title: "Reported!", text: "Anomaly registered.", icon: "success", timer: 1500, showConfirmButton: false });
            } catch (e) {
                Swal.fire("Error", "Could not report anomaly.", "error");
            }
        }
    };

    if (loading) return <CircularProgress sx={{ display: "block", margin: "auto", mt: 10 }} />;
    if (!tripData || !tripData.historicalData?.length) {
        return <SoftTypography variant="body2">Awaiting telemetry data...</SoftTypography>;
    }

    const history = tripData.historicalData;
    const finalPoint = history[history.length - 1];
    const cutoffEpoch = finalPoint.loggedEpoch - (15 * 60);
    const recentHistory = history.filter(d => d.loggedEpoch >= cutoffEpoch);

    const staticArr = Number(matchKey.split("_")[1]);
    const hasUserArrival = !!tripData.arrivalEpoch;
    const isFinalised = hasUserArrival || tripData.isClosed;
    const isActioned = hasUserArrival || tripData.isAnomaly;

    let trueDelay = null;
    let arrivalTimeFormatted = null;

    let chartPoints = recentHistory.map(d => ({
        epoch: d.loggedEpoch,
        label: moment.unix(d.loggedEpoch).tz(TZ).format("HH:mm:ss"),
        feed1: d.feed1Delay,
        feed2: d.feed2Delay
    }));

    const scheduledTimeFormatted = moment.unix(staticArr).tz(TZ).format("HH:mm:ss");

    if (!chartPoints.find(p => p.label === scheduledTimeFormatted)) {
        chartPoints.push({
            epoch: staticArr,
            label: scheduledTimeFormatted,
            feed1: null,
            feed2: null
        });
    }

    if (hasUserArrival) {
        trueDelay = tripData.arrivalEpoch - staticArr;
        arrivalTimeFormatted = moment.unix(tripData.arrivalEpoch).tz(TZ).format("HH:mm:ss");

        if (!chartPoints.find(p => p.label === arrivalTimeFormatted)) {
            chartPoints.push({
                epoch: tripData.arrivalEpoch,
                label: arrivalTimeFormatted,
                feed1: null,
                feed2: null
            });
            chartPoints.sort((a, b) => a.epoch - b.epoch);
        }
    }

    const labels = chartPoints.map(p => p.label);

    // 2. DYNAMICALLY MAP Y-AXIS DATA BASED ON SWITCH STATE
    const feed1Data = chartPoints.map(p => {
        if (p.feed1 === null) return null;
        return showSecondsAway ? (staticArr + p.feed1) - p.epoch : p.feed1;
    });

    const feed2Data = chartPoints.map(p => {
        if (p.feed2 === null) return null;
        return showSecondsAway ? (staticArr + p.feed2) - p.epoch : p.feed2;
    });

    const chartData = {
        labels,
        datasets: [
            { label: "TCB", data: feed1Data, borderColor: "#6B4C9A", backgroundColor: "#6B4C9A", tension: 0.2, pointRadius: 2, spanGaps: true },
            { label: "GTFS-RT", data: feed2Data, borderColor: "#E67E22", backgroundColor: "#E67E22", tension: 0.2, pointRadius: 2, spanGaps: true },
        ],
    };


    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { right: 50 } },
        plugins: {
            legend: { position: "top" },
            annotation: {
                annotations: {
                    // NEW: Scheduled Arrival Line (Blue)
                    scheduledLine: {
                        type: 'line',
                        xMin: scheduledTimeFormatted,
                        xMax: scheduledTimeFormatted,
                        borderColor: '#3b82f6', // Blue
                        borderWidth: 2,
                        borderDash: [4, 4],
                        label: {
                            enabled: showAnnotationLabels, // Fix for v1.2.2
                            display: showAnnotationLabels, // Keep for v2 compatibility
                            content: 'Scheduled',
                            position: 'end', // Places it at the top so it doesn't overlap actual arrival
                            backgroundColor: 'rgba(59, 130, 246, 0.8)'
                        }
                    },

                    // EXISTING: Final Delay Line (Green horizontal)
                    delayLine: (!showSecondsAway && trueDelay !== null) ? {
                        type: 'line',
                        yMin: trueDelay,
                        yMax: trueDelay,
                        borderColor: '#10b981',
                        borderWidth: 2,
                        borderDash: [6, 6],
                        label: {
                            enabled: showAnnotationLabels, // Fix for v1.2.2
                            display: showAnnotationLabels,
                            content: `Final Delay: ${trueDelay}s`,
                            position: 'start',
                            backgroundColor: 'rgba(16, 185, 129, 0.8)'
                        }
                    } : undefined,

                    // EXISTING: Actual Arrival Line (Red vertical)
                    arrivalLine: arrivalTimeFormatted !== null ? {
                        type: 'line',
                        xMin: arrivalTimeFormatted,
                        xMax: arrivalTimeFormatted,
                        borderColor: '#ef4444',
                        borderWidth: 2,
                        borderDash: [6, 6],
                        label: {
                            enabled: showAnnotationLabels, // Fix for v1.2.2
                            display: showAnnotationLabels,
                            content: 'Actual Arrival',
                            position: 'start', // Places it at the bottom
                            yAdjust: -10,
                            backgroundColor: 'rgba(239, 68, 68, 0.8)'
                        }
                    } : undefined,
                }
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const datasetLabel = context.dataset.label || '';
                        const rawValue = context.raw;

                        if (rawValue === null || rawValue === undefined) {
                            return `${datasetLabel}: No Data`;
                        }

                        // 1. Get the exact time this dot was recorded
                        const pointEpoch = chartPoints[context.dataIndex].epoch;

                        // 2. Figure out the math based on which view the switch is in
                        let delay, secondsAway;
                        if (showSecondsAway) {
                            secondsAway = rawValue; // Y-axis is already seconds away
                            delay = secondsAway + pointEpoch - staticArr;
                        } else {
                            delay = rawValue; // Y-axis is delay
                            secondsAway = (staticArr + delay) - pointEpoch;
                        }

                        // 3. Format the display
                        if (true) {
                            return [
                                `${datasetLabel}: ${Math.round(secondsAway)} seconds away`,
                                `(Delay: ${Math.round(delay)}s)`
                            ];
                        } else {
                            // If secondsAway is 0 or negative, it has arrived/passed
                            const timePassed = Math.abs(Math.round(secondsAway));
                            return [
                                `${datasetLabel}: Arrived ${timePassed}s ago`,
                                `(Final Delay: ${Math.round(delay)}s)`
                            ];
                        }
                    }
                }
            }
        },
        scales: {
            y: {
                title: {
                    display: true,
                    text: showSecondsAway ? "Seconds Away" : "Delay (sec)"
                },
                // If viewing Seconds Away, start the axis at 0 (arrival) so it visually counts down to the bottom
                suggestedMin: showSecondsAway ? 0 : (trueDelay !== null ? Math.min(0, trueDelay - 10) : undefined),
            }
        },
        animation: { duration: 0 }
    };

    // 3. Dynamic Scorecard Extractors
    const renderDynamicScorecard = (feedKey, feedName, color, feedStats) => {
        const currentDelay = finalPoint[`${feedKey}Delay`];
        const absolutePredEpoch = currentDelay !== null ? staticArr + currentDelay : null;

        if (isFinalised) {
            return (
                <Card sx={{ borderLeft: `5px solid ${color}`, boxShadow: 3 }}>
                    <SoftBox p={2}>
                        <SoftTypography variant="subtitle2" fontWeight="bold" sx={{ color }}>{feedName} (Finalised)</SoftTypography>
                        <Grid container spacing={1} sx={{ mt: 1 }}>
                            <Grid item xs={4}>
                                <SoftTypography variant="caption" color="text">Avg MAE</SoftTypography>
                                <SoftTypography variant="h6" fontWeight="bold">{feedStats?.mae ?? "-"}s</SoftTypography>
                            </Grid>
                            <Grid item xs={4}>
                                <SoftTypography variant="caption" color="text">Final Err</SoftTypography>
                                <SoftTypography variant="h6" fontWeight="bold">{feedStats?.finalError ?? "-"}s</SoftTypography>
                            </Grid>
                            <Grid item xs={4}>
                                <SoftTypography variant="caption" color="text">Bias</SoftTypography>
                                <SoftTypography variant="h6" fontWeight="bold" sx={{ color: feedStats?.bias < 0 ? "#e53935" : "#4caf50" }}>
                                    {feedStats?.bias ?? "-"}s
                                </SoftTypography>
                            </Grid>
                        </Grid>
                    </SoftBox>
                </Card>
            );
        } else {
            return (
                <Card sx={{ borderLeft: `5px solid ${color}`, boxShadow: 3, bgcolor: "#f8fafc" }}>
                    <SoftBox p={2}>
                        <SoftTypography variant="subtitle2" fontWeight="bold" sx={{ color }}>{feedName} (Live Tracking)</SoftTypography>
                        <Grid container spacing={1} sx={{ mt: 1 }}>
                            <Grid item xs={6}>
                                <SoftTypography variant="caption" color="text">Current Delay</SoftTypography>
                                <SoftTypography variant="h6" fontWeight="bold">
                                    {currentDelay !== null ? `${currentDelay}s` : "No Data"}
                                </SoftTypography>
                            </Grid>
                            <Grid item xs={6}>
                                <SoftTypography variant="caption" color="text">Est. Arrival Time</SoftTypography>
                                <SoftTypography variant="h6" fontWeight="bold">
                                    {/* Enforce timezone here */}
                                    {absolutePredEpoch ? moment.unix(absolutePredEpoch).tz(TZ).format("HH:mm:ss") : "--:--:--"}
                                </SoftTypography>
                            </Grid>
                        </Grid>
                    </SoftBox>
                </Card>
            );
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, height: "100%", overflowY: "auto" }}>

            {/* HEADER: General Trip Info and Action Buttons */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
                <Box>
                    <SoftTypography variant="h5" fontWeight="bold">
                        Route {tripData.routeShortName} {tripData.tripHeadsign}
                    </SoftTypography>
                    <SoftTypography variant="caption" color="text" sx={{ fontFamily: "monospace" }}>
                        Trip Key: {tripData.matchKey}
                    </SoftTypography>
                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                        variant={hasUserArrival ? "contained" : "contained"}
                        size="small"
                        color={hasUserArrival ? "success" : "dark"}
                        disabled={isActioned}
                        onClick={handleTriggerArrival}
                        sx={{ textTransform: "none", minWidth: "120px" }}
                    >
                        {hasUserArrival ? "Arrived ✅" : "Mark Arrived"}
                    </Button>
                    <Button
                        variant={tripData.isAnomaly ? "contained" : "contained"}
                        size="small"
                        color="error"
                        disabled={isActioned}
                        onClick={handleTriggerAnomaly}
                        sx={{ textTransform: "none", minWidth: "120px" }}
                    >
                        {tripData.isAnomaly ? "Reported ⚠️" : "Anomaly"}
                    </Button>
                </Box>
            </Box>

            {/* Meta Information Badges (Now including Scheduled Arrival at all times) */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", px: 1, mt: -1 }}>

                {/* ALWAYS SHOW SCHEDULED ARRIVAL */}
                <SoftTypography variant="caption" sx={{ bgcolor: "#e2e8f0", color: "#334155", px: 1, py: 0.5, borderRadius: 1, fontWeight: "bold" }}>
                    🗓️ Scheduled: {moment.unix(staticArr).tz(TZ).format("MMM DD, YYYY HH:mm:ss")}
                </SoftTypography>

                {isFinalised && hasUserArrival && (
                    <SoftTypography variant="caption" sx={{ bgcolor: "#ecfdf5", color: "#10b981", px: 1, py: 0.5, borderRadius: 1, fontWeight: "bold" }}>
                        ✅ Marked Arrived At: {moment.unix(tripData.arrivalEpoch).tz(TZ).format("MMM DD, YYYY HH:mm:ss")} ({trueDelay}s Delay)
                    </SoftTypography>
                )}
                {isFinalised && tripData.isClosed && (
                    <SoftTypography variant="caption" sx={{ bgcolor: "#f1f5f9", color: "#64748b", px: 1, py: 0.5, borderRadius: 1, fontWeight: "bold" }}>
                        📡 Left Feed At: {moment.unix(finalPoint.loggedEpoch).tz(TZ).format("MMM DD, YYYY HH:mm:ss")}
                    </SoftTypography>
                )}
            </Box>

            {/* Dynamic Scorecards */}
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    {renderDynamicScorecard("feed1", "TCB", "#6B4C9A", tripData.stats?.feed1)}
                </Grid>
                <Grid item xs={12} md={6}>
                    {renderDynamicScorecard("feed2", "GTFS-RT", "#E67E22", tripData.stats?.feed2)}
                </Grid>
            </Grid>

            {/* Chart Section with Y-Axis Switch */}
            {/* Chart Section with Switches */}
            <Box sx={{ display: "flex", flexDirection: "column" }}>

                {/* 3. GROUP BOTH SWITCHES HERE */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, flexWrap: "wrap", mb: 1 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showAnnotationLabels}
                                onChange={(e) => setShowAnnotationLabels(e.target.checked)}
                                color="primary"
                            />
                        }
                        label={
                            <SoftTypography variant="caption" fontWeight="bold">
                                Show Line Labels
                            </SoftTypography>
                        }
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showSecondsAway}
                                onChange={(e) => setShowSecondsAway(e.target.checked)}
                                color="primary"
                            />
                        }
                        label={
                            <SoftTypography variant="caption" fontWeight="bold">
                                View as Seconds Away
                            </SoftTypography>
                        }
                    />
                </Box>

                <Box sx={{ height: 350, position: "relative" }}>
                    <Line data={chartData} options={chartOptions} />
                </Box>
            </Box>

        </Box>
    );

}